import { styled } from "baseui";
import { Badge } from "baseui/badge";
import { Button, SHAPE } from "baseui/button";
import { CustomColumn, DatetimeColumn, StringColumn } from "baseui/data-table";
import { HeadingMedium } from "baseui/typography";
import { useState, useEffect } from "react";
import { useGetContact } from "../api/generated";
import { BaseGrid, GridActions } from "../components/BaseGrid";
import { Content } from "./LayoutPage";
import { useIsPhone } from "../hooks/useIsPhone";
import { ContactsPhone, type PhoneContact } from "./ContactsPhone";
import { formatEventDate } from "./events/eventForm";

const Header = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px",
  borderBottom: "1px solid #e2e8f0",
});

const Main = styled("div", {
  flexGrow: 1,
  padding: "24px",
  backgroundColor: "#fff",
});

export const Contacts = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  
  const { data, isLoading } = useGetContact({ page, limit });
  
  // Extract contacts from response data
  const contacts = data?.data?.data || [];
  const totalPages = data?.data?.total_pages || 1;
  
  // SAM-1308: on a phone the table is a list of cards; the search narrows the rows loaded for the page.
  const isPhone = useIsPhone();
  const [phoneQuery, setPhoneQuery] = useState("");

  const rows = () => {
    try {
      return contacts.map((item: any) => ({
        id: item.id,
        data: item,
      }));
    } catch (error) {
      console.error("Error mapping data:", error);
      return [];
    }
  };
  
  // Reset page when limit changes
  useEffect(() => {
    setPage(1);
  }, [limit]);
  const columns = [
    StringColumn({
      title: "Contact type",
      mapDataToValue: (row) => row.details,
    }),
    StringColumn({
      title: "Guest Name",
      mapDataToValue: (row) =>
        row.main_guest_user_id ? String(row.main_guest_user_id) : "",
    }),
    StringColumn({
      title: "Location",
      mapDataToValue: (row) => row.destination,
    }),
    StringColumn({
      title: "Phone",
      mapDataToValue: (row) => row.phone,
    }),
    CustomColumn({
      title: "Status",
      mapDataToValue: (row) => row.status_id,
      renderCell: (row) => <Badge shape={SHAPE.pill} content={row.value} />,
    }),
    DatetimeColumn({
      title: "Pass from",
      mapDataToValue: (row) => new Date(row.invitation_from),
    }),
    CustomColumn({
      title: "Actions",
      mapDataToValue: (row) => row.actions,
      renderCell: (value) => (
        <GridActions
          value={value}
          onAction={() => { /* read-only for now */ }}
        />
      ),
    }),
  ];

  if (isPhone) {
    const q = phoneQuery.trim().toLowerCase();
    const phoneContacts: PhoneContact[] = contacts
      .map((c: any, i: number) => ({
        id: c.id ?? i,
        title: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.details || "",
        guestId: c.main_guest_user_id,
        destination: c.destination,
        phone: c.phone,
        status: c.status_id != null ? String(c.status_id) : null,
        from: c.invitation_from ? formatEventDate(c.invitation_from) : null,
      }))
      .filter((c) => !q || [c.title, c.destination, c.phone].some((v) => String(v ?? "").toLowerCase().includes(q)));
    return (
      <ContactsPhone
        contacts={phoneContacts}
        loading={isLoading}
        query={phoneQuery}
        onQuery={setPhoneQuery}
        page={page}
        limit={limit}
        // SAM-1312 (U32): without a total from the server, the count is what was loaded (it said «1–20 of 20» with no rows)
        total={data?.data?.total_elements ?? (page - 1) * limit + contacts.length}
        onPage={setPage}
      />
    );
  }

  return (
    <Content>
      <Header>
        <HeadingMedium style={{ margin: 0 }}>Contact list</HeadingMedium>
        <Button size="compact">New Contact</Button>
      </Header>
      <Main>
        <BaseGrid
          loading={isLoading}
          loadingMessage={"Loading contacts..."}
          columns={columns}
          rows={rows()}
          searchable
          filterable={false}
          emptyMessage="No contacts available"
          pagination={{
            numPages: totalPages,
            currentPage: page,
            onPageChange: ({ nextPage }) => {
              if (nextPage !== page) {
                setPage(nextPage);
              }
            },
          }}
        />
      </Main>
    </Content>
  );
};
